import { useState } from "react";
import { Integration } from "@/types/general";
import {
    getReimportConfirmationErrorEntities,
    getRiskyImportEntities,
    getSafeImportEntities,
    isReimportConfirmationError,
} from "./importHistory";

export type ExecuteImportArgs = {
    reimportConfirmed: boolean;
};

type ExecuteImport = (args: ExecuteImportArgs) => Promise<unknown> | unknown;

type PendingImport = {
    plan: Integration.Import.Plan;
    riskyEntities: Integration.Import.Entity[];
    safeEntities: Integration.Import.Entity[];
    execute: ExecuteImport;
    onImportStarted?: () => void;
};

interface UseReimportGuardArgs {
    historyByEntity: Integration.Import.HistoryByEntity;
}

/**
 * Managing the confirmation flow for imports that may duplicate previously imported integration data.
 */
export function useReimportGuard({ historyByEntity }: UseReimportGuardArgs) {
    const [pendingImport, setPendingImport] = useState<PendingImport>();
    const [isConfirmingImport, setIsConfirmingImport] = useState(false);

    /**
     * Creating the pending confirmation state with both risky and safe entity groups.
     */
    const createPendingImport = (
        plan: Integration.Import.Plan,
        execute: ExecuteImport,
        onImportStarted: (() => void) | undefined,
        riskyEntities: Integration.Import.Entity[]
    ): PendingImport => ({
        plan,
        execute,
        onImportStarted,
        riskyEntities,
        safeEntities: getSafeImportEntities(plan, riskyEntities),
    });

    /**
     * Running the import request and converting backend re-import conflicts into confirmation state.
     */
    const runImport = async (
        plan: Integration.Import.Plan,
        execute: ExecuteImport,
        onImportStarted: (() => void) | undefined,
        reimportConfirmed: boolean
    ) => {
        try {
            setIsConfirmingImport(true);
            await execute({ reimportConfirmed });
            setPendingImport(undefined);
            onImportStarted?.();
            return true;
        } catch (error) {
            if (isReimportConfirmationError(error)) {
                setPendingImport(
                    createPendingImport(
                        plan,
                        execute,
                        onImportStarted,
                        getReimportConfirmationErrorEntities(error)
                    )
                );
            }

            return false;
        } finally {
            setIsConfirmingImport(false);
        }
    };

    /**
     * Requesting an import by showing confirmation immediately when local history already marks entities as risky.
     */
    const requestImport = async (
        plan: Integration.Import.Plan,
        execute: ExecuteImport,
        onImportStarted?: () => void
    ) => {
        const riskyEntities = getRiskyImportEntities(plan, historyByEntity);

        if (riskyEntities.length > 0) {
            setPendingImport(createPendingImport(plan, execute, onImportStarted, riskyEntities));
            return false;
        }

        return runImport(plan, execute, onImportStarted, false);
    };

    /**
     * Confirming the pending import by retrying it with the explicit re-import confirmation flag.
     */
    const confirmImport = async () => {
        if (!pendingImport) {
            return false;
        }

        return runImport(
            pendingImport.plan,
            pendingImport.execute,
            pendingImport.onImportStarted,
            true
        );
    };

    /**
     * Cancelling the pending confirmation and returning the modal to its selection state.
     */
    const cancelConfirmation = () => setPendingImport(undefined);

    return {
        pendingImport,
        isConfirmingImport,
        requestImport,
        confirmImport,
        cancelConfirmation,
    };
}
